/**
 * MCP endpoints with health metrics — extends McpEndpointsTab data with API Observe stats.
 */
import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { AppObserveAggregate, McpEndpointRow } from './types';
import {
  aggregateStatsByApp,
  deriveMcpHealthStatus,
  formatNaN,
  mcpHealthLabel,
  summarizeMcpProbeBoard,
} from './helpers';
import type { ObserveStatRow } from './types';
import { hasMcpLastCallStamp, presentMcpCallCount, presentMcpLastCallLabel } from '../mcpEndpointCallHonesty';

type Props = {
  endpoints: McpEndpointRow[] | undefined;
  observeStats: ObserveStatRow[] | undefined;
  isLoading: boolean;
  endpointsError: Error | null;
  statsError: Error | null;
  onRowClick: (endpoint: McpEndpointRow, aggregate: AppObserveAggregate | undefined) => void;
};

function healthChipColor(status: ReturnType<typeof deriveMcpHealthStatus>): 'success' | 'warning' | 'error' | 'default' {
  if (status === 'healthy') return 'success';
  if (status === 'warning' || status === 'quiet') return 'warning';
  if (status === 'critical') return 'error';
  return 'default';
}

export default function McpHealthTable({
  endpoints,
  observeStats,
  isLoading,
  endpointsError,
  statsError,
  onRowClick,
}: Props) {
  const byApp = useMemo(
    () => aggregateStatsByApp(observeStats || []),
    [observeStats]
  );

  const rows = endpoints || [];
  const probeRows = useMemo(
    () => rows.map((ep) => ({ ep, health: deriveMcpHealthStatus(ep, byApp.get(ep.app_id)) })),
    [rows, byApp]
  );
  const board = useMemo(
    () => summarizeMcpProbeBoard(probeRows.map((row) => row.health)),
    [probeRows]
  );

  if (isLoading && !endpoints) {
    return (
      <Box sx={{ mb: 3 }}>
        <Skeleton variant="text" width={180} height={28} />
        <Skeleton variant="rounded" height={200} sx={{ mt: 1 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" gutterBottom>
        Tenant MCP probe board
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Last-call census for every app on this tenant — not this app&apos;s public route health.
        Never, Unknown, and Not provisioned are not failures.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        <Chip size="small" color="error" variant={board.failed ? 'filled' : 'outlined'} label={`Failed ${board.failed}`} />
        <Chip size="small" color="success" variant={board.active ? 'filled' : 'outlined'} label={`Active ${board.active}`} />
        <Chip size="small" color="warning" variant={board.quiet ? 'filled' : 'outlined'} label={`Quiet ${board.quiet}`} />
        <Chip size="small" color="warning" variant={board.degraded ? 'filled' : 'outlined'} label={`Degraded ${board.degraded}`} />
        <Chip size="small" variant="outlined" label={`Never ${board.never}`} />
        <Chip size="small" variant="outlined" label={`Unknown ${board.unknown}`} />
        <Chip size="small" variant="outlined" label={`Not provisioned ${board.not_provisioned}`} />
      </Box>
      {endpointsError && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          MCP inventory unavailable: {endpointsError.message}
        </Alert>
      )}
      {statsError && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Observe stats unavailable — error rate and latency will show N/A. ({statsError.message})
        </Alert>
      )}
      <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>App</strong></TableCell>
              <TableCell><strong>Endpoint</strong></TableCell>
              <TableCell align="right"><strong>Calls</strong></TableCell>
              <TableCell><strong>Last call</strong></TableCell>
              <TableCell align="right"><strong>Error 24h</strong></TableCell>
              <TableCell align="right"><strong>Avg latency</strong></TableCell>
              <TableCell align="center"><strong>Probe</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No MCP endpoints found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              probeRows.map(({ ep, health }) => {
                const agg = byApp.get(ep.app_id);
                return (
                  <TableRow
                    key={ep.app_id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => onRowClick(ep, agg)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{ep.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {ep.app_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', maxWidth: 200, display: 'block' }} noWrap>
                        {ep.mcp_provisioned ? ep.mcp_endpoint : 'Not provisioned'}
                      </Typography>
                      {ep.last_mcp_tool && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          last: {ep.last_mcp_tool}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{presentMcpCallCount(ep)}</TableCell>
                    <TableCell>
                      {hasMcpLastCallStamp(ep)
                        ? presentMcpLastCallLabel(ep)
                        : <Typography variant="caption" color="text.secondary">{presentMcpLastCallLabel(ep)}</Typography>}
                    </TableCell>
                    <TableCell align="right">{formatNaN(agg?.error_rate_24h, '%')}</TableCell>
                    <TableCell align="right">{formatNaN(agg?.avg_latency_ms, 'ms')}</TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={mcpHealthLabel(health)} color={healthChipColor(health)} />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
